import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Subscribes to the FlowFinder backend SSE stream and exposes a keyed map
 * of live quotes plus connection metadata.
 *
 * Returns:
 *   quotes        Object<ticker, { price, change_pct, status, source, age_sec, ts_ms, name, asset_class }>
 *   connected     boolean — true while EventSource.readyState === OPEN
 *   wsConnected   boolean — backend says the Finnhub WS is up
 *   liveCount     number  — how many tickers have a fresh live tick
 *   total         number  — total tickers in the universe
 *   lastEventAt   number  — local ms timestamp of the last SSE message
 *   reconnect()   () => void — force-reconnect
 */
export default function useLiveQuotes() {
  const [quotes, setQuotes] = useState({});
  const [connected, setConnected] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [liveCount, setLiveCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [lastEventAt, setLastEventAt] = useState(0);
  const esRef = useRef(null);
  const backoffRef = useRef(1500);

  const open = useCallback(() => {
    if (esRef.current) {
      try { esRef.current.close(); } catch (err) { console.debug("[useLiveQuotes] close error:", err); }
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/scanner/stream/quotes`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;
    es.onopen = () => {
      setConnected(true);
      backoffRef.current = 1500;
      // eslint-disable-next-line no-console
      console.log("[FlowFinder] SSE connected — subscribed to Finnhub live quote stream");
    };
    es.onmessage = (evt) => {
      try {
        const snap = JSON.parse(evt.data);
        const next = {};
        let stale = 0;
        for (const row of snap.rows || []) {
          next[row.ticker] = row;
          if (row.status === "stale") stale += 1;
        }
        setQuotes((prev) => {
          // Only replace rows whose ts_ms is >= what we already have. This
          // guarantees a REST-seeded snapshot never overwrites a fresher WS
          // tick — the same "WS wins" rule the backend enforces, mirrored
          // client-side just in case of out-of-order events.
          const merged = { ...prev };
          for (const [k, row] of Object.entries(next)) {
            const cur = merged[k];
            if (!cur || (row.ts_ms || 0) >= (cur.ts_ms || 0)) merged[k] = row;
          }
          // drop rows that vanished from the universe
          for (const k of Object.keys(merged)) if (!next[k]) delete merged[k];
          return merged;
        });
        setWsConnected(!!snap.ws_connected);
        setLiveCount(snap.live_count || 0);
        setTotal(snap.total || (snap.rows || []).length);
        setLastEventAt(Date.now());
        if (stale > 0) {
          // eslint-disable-next-line no-console
          console.warn(`[FlowFinder] ${stale} stale symbol(s) detected`);
        }
      } catch (e) {
        // ignore malformed SSE events — keep the connection alive
        console.debug("[useLiveQuotes] malformed event:", e);
      }
    };
    es.onerror = () => {
      setConnected(false);
      try { es.close(); } catch (err) { console.debug("[useLiveQuotes] close error:", err); }
      const wait = Math.min(15000, backoffRef.current);
      backoffRef.current = Math.min(15000, backoffRef.current * 1.6);
      // eslint-disable-next-line no-console
      console.warn(`[FlowFinder] SSE disconnected — reconnecting in ${Math.round(wait)}ms`);
      setTimeout(open, wait);
    };
  }, []);

  useEffect(() => {
    open();
    return () => {
      try { esRef.current && esRef.current.close(); } catch (err) { console.debug("[useLiveQuotes] unmount close error:", err); }
    };
  }, [open]);

  return { quotes, connected, wsConnected, liveCount, total, lastEventAt, reconnect: open };
}
